import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  api,
  AppShell,
  BrandMark,
  endSession,
  SIGN_OUT_FAILED_MESSAGE,
  Icon,
  navLinkClass,
  type NavVariant,
} from '@dataroom/shared';
import { useSavedChecklists } from '@/api/hooks';
import { OwnershipRecoveryBanner } from './OwnershipRecoveryBanner';
import { TrialBanner } from './TrialBanner';

/**
 * The admin app chrome: the shared AppShell (one header, one 1200px container,
 * nav collapsing to a drawer under 768px) with the admin's top-level sections,
 * Help (the help center, which also offers Contact support) and Sign out, and the
 * trial banner under the header.
 */
export function Layout() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Saved checklists are tenant-admin only; a successful read is the one client-
  // side signal of that capability, so the Checklists link shows only then.
  const savedChecklists = useSavedChecklists();
  // The platform owner's console (ADR-0042): the API says whether THIS actor is
  // the platform owner; everyone else never sees the link (and gets 404 there).
  const platform = useQuery({
    queryKey: ['platform', 'me'],
    queryFn: () => api.get<{ isPlatformOwner: boolean }>('/platform/me'),
    retry: false,
    staleTime: 5 * 60_000,
  });

  function signOut() {
    void endSession(qc).then((ended) => {
      if (ended) navigate('/login');
      else window.alert(SIGN_OUT_FAILED_MESSAGE);
    });
  }

  // No session/auth-context hook exposes the signed-in actor's tenantRole to the
  // SPA yet, so Team and Settings stay visible for everyone; a non-owner hitting
  // them sees a plain access message instead (TeamPage / SettingsPage handle the 403).
  // `reloadDocument`: the keys console is entered by a FULL page load, never a
  // client-side route change, so its strict CSP (public/_headers) applies and
  // the analytics tag loaded on other pages is gone (ADR-0069 W12).
  const links: { to: string; label: string; end?: boolean; reloadDocument?: boolean }[] = [
    { to: '/', label: 'Rooms', end: true },
    ...(savedChecklists.isSuccess ? [{ to: '/checklists', label: 'Checklists' }] : []),
    { to: '/team', label: 'Team' },
    { to: '/billing', label: 'Billing' },
    { to: '/settings', label: 'Settings' },
    ...(platform.data?.isPlatformOwner
      ? [
          { to: '/platform/affiliates', label: 'Affiliates' },
          { to: '/platform/ownership-recovery', label: 'Recoveries' },
          { to: '/platform/keys', label: 'Keys', reloadDocument: true },
        ]
      : []),
  ];

  const renderNav = (variant: NavVariant, close: () => void) =>
    links.map((link) => (
      <NavLink
        key={link.to}
        to={link.to}
        end={link.end}
        reloadDocument={link.reloadDocument}
        onClick={close}
        className={({ isActive }) => navLinkClass(variant, isActive)}
      >
        {link.label}
      </NavLink>
    ));

  const renderActions = (variant: NavVariant, close: () => void) => (
    <>
      <NavLink
        to="/help"
        onClick={close}
        // In the bar the word shows from 1024px; between 768 and 1024 it is the
        // icon alone, so the header never wraps.
        aria-label={variant === 'bar' ? 'Help' : undefined}
        className={({ isActive }) => navLinkClass(variant, isActive)}
      >
        <Icon name="help" />
        <span className={variant === 'bar' ? 'hidden lg:inline' : undefined}>Help</span>
      </NavLink>
      <button
        type="button"
        className={navLinkClass(variant, false)}
        onClick={() => {
          close();
          signOut();
        }}
      >
        Sign out
      </button>
    </>
  );

  return (
    <AppShell
      brand={
        <Link to="/" aria-label="ClosingVault Admin — all rooms" className="rounded-md">
          <BrandMark appLabel="Admin" />
        </Link>
      }
      renderNav={renderNav}
      renderActions={renderActions}
      banner={
        <>
          {/* ADR-0055 §4: a recovery waiting out its 72 hours, for every owner. */}
          <OwnershipRecoveryBanner />
          <TrialBanner />
        </>
      }
    >
      <main>
        <Outlet />
      </main>
    </AppShell>
  );
}
